import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const fieldKeySlug = (key) =>
  String(key ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "field";

const DynamicForm = ({
  formData,
  defaultFields = [],
  onFormChange,
  initialValues = {},
  testIdPrefix = "dynamic-form",
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  const isDateField = (fieldName) => {
    const fieldConfig = defaultFields.find((field) => field.key === fieldName);
    return fieldConfig?.data_type === "date";
  };

  const isFieldRequired = (fieldName) => {
    const fieldConfig = defaultFields.find((field) => field.key === fieldName);
    return fieldConfig?.is_required === true;
  };

  const getFieldType = (fieldName) => {
    const fieldConfig = defaultFields.find((field) => field.key === fieldName);

    if (fieldConfig?.data_type === "date") {
      return "date";
    }

    if (fieldConfig?.data_type === "number") {
      return "number";
    }

    return "text";
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";

    try {
      if (typeof dateValue === "string") {
        const datePart = dateValue.split("T")[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          return datePart;
        }
      }

      const date = new Date(dateValue);

      if (!isNaN(date.getTime())) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error("Date formatting error:", e);
    }

    return "";
  };

  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && !isInitialized) {
      const initialFormValues = {};

      Object.keys(formData).forEach((key) => {
        let value = initialValues[key] ?? formData[key] ?? "";

        if (isDateField(key) && value) {
          value = formatDateForInput(value);
        }

        initialFormValues[key] = value;
      });

      setFormValues(initialFormValues);
      setIsInitialized(true);
    }
  }, [formData, initialValues, isInitialized, defaultFields]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    let processedValue = value;

    if (type === "number" && value !== "") {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        processedValue = numValue;
      }
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (onFormChange) {
      onFormChange({ ...formValues, [name]: processedValue });
    }
  };

  const validateField = (name, value) => {
    if (isFieldRequired(name)) {
      if (value === null || value === undefined || value === "") {
        return t("verificationApprovals:field_required");
      }

      const stringValue = String(value);
      if (stringValue.trim() === "") {
        return t("verificationApprovals:field_required");
      }
    }

    return "";
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const value = formValues[name];
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const renderField = (fieldName, fieldValue, label) => {
    const error = errors[fieldName];
    const fieldType = getFieldType(fieldName, fieldValue);
    const currentValue = formValues[fieldName] ?? "";
    const isRequired = isFieldRequired(fieldName);

    const displayLabel =
      label ||
      fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    return (
      <div
        key={fieldName}
        className="mb-4"
        id={`${testIdPrefix}-field-${fieldKeySlug(fieldName)}`}
      >
        <label
          htmlFor={fieldName}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {displayLabel}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type={fieldType}
          name={fieldName}
          id={`${testIdPrefix}-input-${fieldKeySlug(fieldName)}`}
          value={currentValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? "border-red-500" : "border-gray-300"
          }`}
          required={isRequired}
          min={fieldType === "date" ? "1900-01-01" : undefined}
          max={fieldType === "date" ? "2100-12-31" : undefined}
          step={fieldType === "number" ? "any" : undefined}
          placeholder={`Enter ${displayLabel.toLowerCase()}`}
        />
        {error && (
          <p
            className="mt-1 text-sm text-red-600 flex items-center"
            id={`${testIdPrefix}-error-${fieldKeySlug(fieldName)}`}
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
      </div>
    );
  };

  if (!formData || Object.keys(formData).length === 0) {
    return (
      <div className="text-center py-4" id={`${testIdPrefix}-empty`}>
        <p className="text-gray-500">
          {t("verificationApprovals:no_form_data_available")}
        </p>
      </div>
    );
  }

  const fieldKeys = Object.keys(formData);

  return (
    <div className="space-y-4" id={`${testIdPrefix}-fields`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fieldKeys.map((key) => {
          const label = key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          return renderField(key, formData[key], label);
        })}
      </div>
    </div>
  );
};

export default DynamicForm;
